import localeJP from '@angular/common/locales/ja';
import { ADDRESS_SERVICE_URL } from '@lang/jp/app.config';
import { PhaseJpService } from '@lang/jp/services/phasejp.service';
import { NgModule, LOCALE_ID, ErrorHandler } from '@angular/core';
import {
    registerLocaleData,
    PlatformLocation,
    APP_BASE_HREF,
} from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import {
    provideHttpClient,
    withInterceptorsFromDi,
} from '@angular/common/http';
import { NgbModule, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FileUploadModule } from 'ng2-file-upload';
import { NgxMaskModule } from 'ngx-mask';
import {
    OAuthModule,
    OAuthStorage,
    ValidationHandler,
    AuthConfig,
    OAuthModuleConfig,
} from 'angular-oauth2-oidc';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';

import {
    API_URL,
    CMS_URL,
    CMS_ALLOW_LINKS,
    CMS_PRODUCT,
    CMS_LOCALE,
    BUSINESS,
    CMS_REGION,
    CANONICAL_COMMUNITY,
    COOKIE_POLICY_URL,
    ANALYTICS_API_URL,
    DEFAULT_COMMUNITY,
    ANALYTICS_LOG_TO_CONSOLE,
    ANALYTICS_SEND_TO_TAGMANAGER,
    CAN_ACTIVATE_PREFERENCE_CENTER,
    APPLICATION_INSIGHTS_CONNECTION_STRING,
} from '@common/app/app.config';

import { CHECK_DUPLICATED_REGISTRATIONS, IS_PRODUCTION } from './spjp.config';

import { SharedModule } from './shared.module';
import { AppCommonModule } from '@common/app-common.module';

import { ContentService } from '@common/services/content.service';
import { GoogleAnalyticsService } from '@common/services/google-analytics.service';
import { SeoService } from '@common/services/seo.service';
import { KenalyticsService } from '@common/services/kenalytics.service';
import { AuthService } from '@common/services/auth.service';
import { InMemoryQueryService } from '@common/services/inmemory.query.service';
import { RouteService } from '@common/infrastructure/params-service';
import { LinkService } from '@common/services/link.service';
import { CommandService } from '@common/services/command.service';
import { ThemeService } from '@common/services/theme.service';
import { SpJpMonitoringService } from '@spjp/services/monitoring.service';

import { PendingChangesGuard } from '@common/infrastructure/pending-changes.guard';
import { InMemoryAuthStorage } from '@common/services/in-memory-auth-storage';
import { CommunityService } from '@common/services/community.service';

import { environment } from '../environments/environment';
import { RegistrationService } from './services/registration.service';
import { SolarService } from './services/solar/solar.service';
import { ErrorHandlerService } from './services/error-handler.service';
import { ContentHelperService } from './services/contenthelper.service';
import { CommunityGuard } from './guards/community.guard';
import { RedirectGuard } from './guards/redirect.guard';
import { AuthGuard } from './guards/auth.guard';
import { ParticipantStatusGuard } from './guards/participant-status.guard';
import { SpjpCommunityService } from './services/community.service';

import { AppComponent } from './app.component';
import { MenuComponent } from './components/menu/menu.component';
import { FooterComponent } from './components/footer/footer.component';
import { HomeComponent } from './components/home/home.component';
import { AnalyticsEnricherProvider } from '@common/analytics/services/analytics-enricher.service';
import { WINDOW_PROVIDERS } from '@common/services/window.service';

import { AnalyticsSolver } from './solvers/analytics.solver';
import { AnalyticsResolverProvider } from '@common/analytics/resolvers/analytics.resolver';
import { ProcessStepService } from './services/process-step.service';
import { ProcessGoalEventFactory } from '@common/analytics/services/process-goal-event-factory.service';
import { AppRoutingModule } from './app-routing.module';
import { CommunityRedirectService } from '@common/services/community-redirect.service';
import { WebinarInterestedIndividualsComponent } from '@spjp/components/home/webinar/interested-idividuals/webinar-interested-individuals.component';
import { WebinarFullRegistrationComponent } from '@spjp/components/home/webinar/full-registration/webinar-full-registration.component';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { SvgIconWrapperService } from './services/svg-icon-wrapper.service';

import { AppConfigService, APP_CONFIG } from './services/app-config.service';
import { AppConfig } from './model/app-config';
import {
    authConfigFactory,
    getBaseHref,
    getCmsApiUrl,
    getDefaultCommunity,
} from './services/app-module-helpers';
import { MonitoringService } from '@common/services/monitoring.service';
import { SupplierService } from '@common/services/supplier.service';
import { SpjpAuctionService } from '@spjp/services/auction.service';
import { AuctionService } from '@common/services/auction.service';

registerLocaleData(localeJP, 'ja-JP');

@NgModule({
    declarations: [
        AppComponent,
        MenuComponent,
        FooterComponent,
        HomeComponent,
        WebinarInterestedIndividualsComponent,
        WebinarFullRegistrationComponent,
    ],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        SharedModule,
        OAuthModule.forRoot(),
        AppRoutingModule,
        NgbModule,
        FileUploadModule,
        AppCommonModule,
        NgxMaskModule.forRoot(),
        AngularSvgIconModule.forRoot(),
    ],
    providers: [
        {
            provide: AppConfigService,
            useFactory: (config: AppConfig) => new AppConfigService(config),
            deps: [APP_CONFIG],
        },
        ContentHelperService,
        SpjpCommunityService,
        { provide: CommunityService, useExisting: SpjpCommunityService },
        SpjpAuctionService,
        { provide: AuctionService, useExisting: SpjpAuctionService },
        RouteService,
        LinkService,
        CommandService,
        RegistrationService,
        SolarService,
        ContentService,
        PendingChangesGuard,
        GoogleAnalyticsService,
        SeoService,
        AuthService,
        KenalyticsService,
        InMemoryQueryService,
        ThemeService,
        CommunityGuard,
        RedirectGuard,
        AuthGuard,
        ParticipantStatusGuard,
        {
            provide: APPLICATION_INSIGHTS_CONNECTION_STRING,
            useFactory: () => environment.aiConnectionString,
            deps: [AppConfigService],
        },
        {
            provide: MonitoringService,
            useClass: SpJpMonitoringService,
            deps: [BUSINESS, APPLICATION_INSIGHTS_CONNECTION_STRING],
        },
        PhaseJpService,
        NgbActiveModal,
        ProcessGoalEventFactory,
        CommunityRedirectService,
        WINDOW_PROVIDERS,
        { provide: LOCALE_ID, useValue: 'ja-JP' },
        {
            provide: API_URL,
            useFactory: () => environment.apiUri,
            deps: [AppConfigService],
        },
        {
            provide: CMS_URL,
            useFactory: getCmsApiUrl,
            deps: [PlatformLocation],
        },
        {
            provide: CMS_ALLOW_LINKS,
            useFactory: () => environment.allowLinksToCms,
            deps: [AppConfigService],
        },
        { provide: CMS_PRODUCT, useValue: 'solarpanels' },
        { provide: CMS_REGION, useValue: 'jp' },
        { provide: CMS_LOCALE, useValue: 'ja' },
        { provide: BUSINESS, useValue: 'spjp' },
        { provide: OAuthStorage, useClass: InMemoryAuthStorage },
        { provide: ValidationHandler, useClass: JwksValidationHandler },
        {
            provide: AuthConfig,
            useFactory: authConfigFactory,
            deps: [PlatformLocation, AppConfigService],
        },
        {
            provide: OAuthModuleConfig,
            useFactory: (): OAuthModuleConfig => {
                return {
                    resourceServer: {
                        allowedUrls: [environment.apiUri],
                        sendAccessToken: true,
                    },
                };
            },
            deps: [AppConfigService],
        },
        {
            provide: ANALYTICS_API_URL,
            useFactory: () => environment.analyticsUri,
            deps: [AppConfigService],
        },
        {
            provide: COOKIE_POLICY_URL,
            useValue:
                'https://consent.cookiebot.com/c3ac3107-b4a9-4a39-9d07-f3540de67401/cd.js',
        },
        {
            provide: ADDRESS_SERVICE_URL,
            useFactory: () => environment.addressServiceUri,
            deps: [AppConfigService],
        },
        {
            provide: CHECK_DUPLICATED_REGISTRATIONS,
            useFactory: () => environment.checkDuplicatedRegistrations,
            deps: [AppConfigService],
        },
        {
            provide: IS_PRODUCTION,
            useFactory: () => environment.production,
            deps: [AppConfigService],
        },
        {
            provide: CAN_ACTIVATE_PREFERENCE_CENTER,
            useFactory: () => environment.canAccessPreferenceCenter,
            deps: [AppConfigService],
        },
        { provide: ErrorHandler, useClass: ErrorHandlerService },
        {
            provide: APP_BASE_HREF,
            useFactory: getBaseHref,
            deps: [PlatformLocation],
        },
        {
            provide: DEFAULT_COMMUNITY,
            useFactory: getDefaultCommunity,
            deps: [PlatformLocation],
        },
        { provide: CANONICAL_COMMUNITY, useValue: 'kanagawa' },
        ProcessStepService,
        AnalyticsSolver,
        {
            provide: AnalyticsResolverProvider,
            deps: [RegistrationService, CommunityService, AnalyticsSolver],
        },
        {
            provide: AnalyticsEnricherProvider,
            deps: [RegistrationService, SpjpCommunityService],
        },
        {
            provide: ANALYTICS_SEND_TO_TAGMANAGER, // should be removed when fully moved to GA4
            useFactory: () => true,
        },
        {
            provide: ANALYTICS_LOG_TO_CONSOLE, // should be removed when fully moved to GA4
            useFactory: () => !environment.production,
            deps: [AppConfigService],
        },
        SvgIconWrapperService,
        SupplierService,
        provideHttpClient(withInterceptorsFromDi()),
    ],
})
export class AppModule {}
